import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Bell, PaperPlaneTilt, TestTube, Lightning, Eye } from "@phosphor-icons/react";
import { enablePush, isPushSupported, permissionState, isIosLike, isStandalone } from "../../lib/push";

/**
 * AdminPushPanel — Phase A admin tooling for push notifications.
 *
 *  - Stats: subscribed users, sends in last 24h, push_enabled flag
 *  - Test-to-self button: fires a notification to the admin's own browser
 *    (useful for sanity checks after deploy)
 *  - Manual broadcast form with mandatory dry-run preview before send
 */
export default function AdminPushPanel() {
  const [stats, setStats] = useState(null);
  const [enabling, setEnabling] = useState(false);
  const [perm, setPerm] = useState("default");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("opted_in");
  const [url, setUrl] = useState("/map");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    api
      .get("/admin/push/stats")
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));
  };

  useEffect(() => {
    refresh();
    if (isPushSupported()) setPerm(permissionState());
  }, []);

  const onEnableThisBrowser = async () => {
    setEnabling(true);
    const res = await enablePush();
    setEnabling(false);
    if (res.ok) {
      setPerm("granted");
      toast.success("This browser is now subscribed");
      refresh();
    } else if (res.reason === "ios_needs_pwa") {
      toast.error("On iPhone, add the site to your Home Screen first, then enable from there.");
    } else if (res.reason === "denied") {
      toast.error("Notifications blocked. Re-enable in browser settings.");
    } else if (res.reason === "unsupported") {
      toast.error("Push isn't supported in this browser.");
    } else {
      toast.error(`Couldn't enable: ${res.reason || "unknown"}`);
    }
  };

  const onTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push/test", {});
      toast.success(`Test sent — delivered to ${data.sent}/${data.subscriptions_tried}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e) || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  // "Why didn't any pushes go out?" diagnostic — runs the same code
  // path as the every-5-min truck-live cron and returns its counters
  // verbatim. Lets the admin see at a glance whether:
  //   - trucks_checked is 0   → no truck is currently live (real cause)
  //   - skipped_no_followers  → trucks are live but nobody bell-tapped
  //   - blocked: {…}          → rules engine is muting everyone
  //   - users_sent > 0        → actually delivered (great!)
  const [lastRun, setLastRun] = useState(null);
  const onRunTruckLive = async () => {
    setBusy(true);
    setLastRun(null);
    try {
      const { data } = await api.post("/admin/push/run-truck-live", {});
      setLastRun(data);
      if (data.users_sent > 0) {
        toast.success(`Sent to ${data.users_sent} users · ${data.subs_sent} subscriptions`);
      } else if (data.trucks_checked === 0) {
        toast.message("No trucks are currently live — nothing to announce.");
      } else {
        toast.message(`${data.trucks_checked} live trucks; no pushes fired (see breakdown below).`);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e) || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  const onPreview = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push/broadcast", {
        title, body, url, target, dry_run: true,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const onSend = async () => {
    if (!preview) return;
    if (!window.confirm(`Send to ${preview.would_send_to} subscribers? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push/broadcast", {
        title, body, url, target, dry_run: false,
      });
      toast.success(`Sent · delivered ${data.sent} · failed ${data.failed}`);
      setPreview(null);
      setTitle("");
      setBody("");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e) || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="admin-push-panel">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-base inline-flex items-center gap-2">
            <Bell weight="duotone" className="h-5 w-5 text-primary" />
            Push notifications
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Admin only · {stats?.push_enabled ? "Active (PUSH_ENABLED=true)" : "Disabled (PUSH_ENABLED=false — sends will no-op)"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onRunTruckLive}
            disabled={busy}
            className="h-9 px-3 rounded-full border border-border bg-card text-xs font-semibold hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="admin-push-run-truck-live"
            title="Run the truck-live cron now and show what it would do"
          >
            <Lightning weight="duotone" className="h-3.5 w-3.5" />
            Diagnose
          </button>
          <button
            type="button"
            onClick={onTest}
            disabled={busy || !stats?.subscriptions_active}
            className="h-9 px-3 rounded-full border border-border bg-card text-xs font-semibold hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid="admin-push-test"
            title="Send a test notification to your own subscriptions"
          >
            <TestTube weight="duotone" className="h-3.5 w-3.5" />
            Test to me
          </button>
        </div>
      </header>

      {/* Stats grid — top row is "supply", bottom is "demand + delivery" */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="admin-push-stats">
        <Stat label="Subscribed" value={stats?.subscriptions_active ?? "…"} />
        <Stat label="Linked to user" value={stats?.subscriptions_linked_to_user ?? "…"} />
        <Stat label="Bell-tapped trucks" value={stats?.follows_total ?? "…"} />
        <Stat label="Subs that follow ≥1 truck" value={stats?.subscriptions_with_follows ?? "…"} />
        <Stat label="Live trucks right now" value={stats?.live_trucks_now ?? "…"} />
        <Stat label="Sent · 24h" value={stats?.sent_24h ?? "…"} />
        <Stat label="Clicked · 24h" value={stats?.clicked_24h ?? "…"} icon={Eye} />
        <Stat label="Suppressed · 24h" value={stats?.suppressed_24h ?? "…"} />
      </div>

      {/* Webpush() failure reasons — shows up automatically when at
          least one active sub has a stored last_failure_reason. This
          is the single most useful diagnostic for "Test to me said
          0 of N delivered" — VAPID mismatch, encryption errors,
          quota, etc. all surface here with a count. */}
      {stats?.recent_failures?.length > 0 && (
        <div
          className="rounded-xl border border-rose-500/40 bg-rose-500/5 p-3 space-y-1.5"
          data-testid="admin-push-failures"
        >
          <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700 dark:text-rose-300">
            Recent webpush() failures · active subs
          </div>
          <ul className="text-xs space-y-1">
            {stats.recent_failures.map((f, i) => (
              <li key={i} className="break-all">
                <span className="font-bold tabular-nums text-rose-800 dark:text-rose-200">
                  ×{f.count}
                </span>{" "}
                {f.status != null && (
                  <span className="font-mono opacity-70">[HTTP {f.status}]</span>
                )}{" "}
                <span className="opacity-80">{f.reason || "(no reason captured)"}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-rose-800/80 dark:text-rose-300/80 italic mt-1">
            If you see <code>InvalidArgument</code> / <code>signature</code> errors here, the
            subscriptions were created under a different VAPID public
            key than the server is signing with now. Users must
            unsubscribe + re-bell-tap to fix.
          </p>
        </div>
      )}

      {/* "Why didn't anything fire?" inline diagnostic — shows up
          only after the admin hits the Diagnose button. Renders the
          truck-live cron's raw counters so the cause is unambiguous. */}
      {lastRun && (        <div
          className="rounded-xl border border-border bg-muted/10 p-3 space-y-1 text-xs"
          data-testid="admin-push-diagnose-result"
        >
          <div className="font-semibold text-sm mb-1.5">
            Truck-live cron · just ran
          </div>
          <DiagnoseStat label="Live trucks checked" value={lastRun.trucks_checked} />
          <DiagnoseStat label="Trucks announced (had ≥1 follower)" value={lastRun.trucks_announced} />
          <DiagnoseStat label="Pushes actually sent" value={lastRun.subs_sent} highlight={lastRun.subs_sent > 0} />
          <DiagnoseStat label="Users reached" value={lastRun.users_sent} highlight={lastRun.users_sent > 0} />
          {lastRun.skipped_no_followers > 0 && (
            <DiagnoseStat
              label="Skipped — no follower bell-tapped these trucks"
              value={lastRun.skipped_no_followers}
              warn
            />
          )}
          {lastRun.skipped_already_delivered > 0 && (
            <DiagnoseStat
              label="Skipped — already delivered for this live session"
              value={lastRun.skipped_already_delivered}
            />
          )}
          {lastRun.blocked && Object.keys(lastRun.blocked).length > 0 && (
            <div className="mt-1.5 pt-1.5 border-t border-border">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                Rules-engine blocks
              </div>
              {Object.entries(lastRun.blocked).map(([k, v]) => (
                <DiagnoseStat key={k} label={k} value={v} warn />
              ))}
            </div>
          )}
          {lastRun.trucks_checked === 0 && (
            <p className="mt-2 text-[11px] text-muted-foreground italic">
              No truck has a <code>starts_at</code> in the last 2 hours with{" "}
              <code>ends_at</code> still in the future. The cron has nothing to
              announce. Pushes only fire when a real owner posts an event that
              is live RIGHT NOW.
            </p>
          )}
        </div>
      )}

      {/* Top suppression reasons — actionable signal when one rule
          dominates (>50% of intended sends). Quietly hidden when the
          rules engine has nothing to report. */}
      {stats?.top_blocked_24h?.length > 0 && (
        <SuppressionInsight
          top={stats.top_blocked_24h}
          dominant={stats.dominant_block_24h}
          totalSuppressed={stats.suppressed_24h}
        />
      )}

      {/* Enable for this browser */}
      {isPushSupported() && perm !== "granted" && (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-3 text-sm" data-testid="admin-push-enable-self">
          <div className="font-semibold mb-1">Subscribe this browser first</div>
          <p className="text-xs text-muted-foreground mb-2">
            You need an active subscription on this device before "Test to me" works.
            {isIosLike() && !isStandalone() && (
              <span className="block mt-1 text-rose-600 dark:text-rose-400 font-semibold">
                On iPhone Safari, add the site to your Home Screen first, then enable from there.
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={onEnableThisBrowser}
            disabled={enabling}
            className="h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="admin-push-enable-btn"
          >
            <Lightning weight="duotone" className="h-3.5 w-3.5" />
            Enable on this browser
          </button>
        </div>
      )}

      {/* Manual broadcast form */}
      <div className="rounded-xl border border-border bg-muted/10 p-3 space-y-2" data-testid="admin-push-broadcast">
        <h3 className="font-display font-bold text-sm">Manual broadcast</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <input
            type="text"
            value={title}
            onChange={(e) => { setTitle(e.target.value); setPreview(null); }}
            placeholder="Title (e.g. Food Truck Friday)"
            maxLength={80}
            className="h-9 px-3 rounded-full border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="admin-push-title"
          />
          <select
            value={target}
            onChange={(e) => { setTarget(e.target.value); setPreview(null); }}
            className="h-9 px-3 rounded-full border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="admin-push-target"
          >
            <option value="opted_in">Opted-in users (linked to account)</option>
            <option value="all">Everyone subscribed (incl. anon)</option>
            <option value="self">Just me (test)</option>
          </select>
        </div>
        <input
          type="text"
          value={body}
          onChange={(e) => { setBody(e.target.value); setPreview(null); }}
          placeholder="Body (e.g. 15 trucks live today — pick a favorite)"
          maxLength={240}
          className="h-9 w-full px-3 rounded-full border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="admin-push-body"
        />
        <input
          type="text"
          value={url}
          onChange={(e) => { setUrl(e.target.value); setPreview(null); }}
          placeholder="Click destination (default /map)"
          maxLength={200}
          className="h-9 w-full px-3 rounded-full border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          data-testid="admin-push-url"
        />

        {preview ? (
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm" data-testid="admin-push-preview">
            <div className="font-semibold text-emerald-700 dark:text-emerald-300 mb-1">
              Preview · would send to {preview.would_send_to} subscribers
            </div>
            {preview.sample_recipients?.length > 0 && (
              <div className="text-xs text-muted-foreground">
                e.g. {preview.sample_recipients.slice(0, 5).join(", ")}{preview.sample_recipients.length > 5 ? "…" : ""}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={onSend}
                disabled={busy || preview.would_send_to === 0}
                className="h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="admin-push-send"
              >
                <PaperPlaneTilt weight="duotone" className="h-3.5 w-3.5" />
                {busy ? "Sending…" : "Send for real"}
              </button>
              <button
                type="button"
                onClick={() => setPreview(null)}
                disabled={busy}
                className="h-9 px-3 rounded-full border border-border bg-card text-xs font-semibold hover:bg-muted"
                data-testid="admin-push-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onPreview}
            disabled={busy || !title.trim() || !body.trim()}
            className="h-9 px-4 rounded-full bg-card border border-border text-xs font-semibold hover:bg-muted disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="admin-push-preview-btn"
          >
            Preview recipients
          </button>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
        {Icon && <Icon weight="duotone" className="h-3 w-3" />}
        {label}
      </div>
      <div className="mt-0.5 font-display font-bold text-xl leading-none">{value}</div>
    </div>
  );
}

function DiagnoseStat({ label, value, highlight, warn }) {
  const tone = highlight
    ? "text-emerald-700 dark:text-emerald-300 font-bold"
    : warn
      ? "text-amber-700 dark:text-amber-300 font-semibold"
      : "text-foreground";
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm tabular-nums ${tone}`}>{value ?? 0}</span>
    </div>
  );
}



// Human-readable copy for each rules-engine block reason. The ACTION
// strings are the single most useful thing on the dashboard once the
// notifications are firing — they tell the admin exactly what to tune.
const REASON_COPY = {
  quiet_hours: { label: "Quiet hours", action: "Soften the 9am–8pm window if engagement spikes are happening earlier/later." },
  daily_cap: { label: "3-per-day cap", action: "Raise the per-user daily cap if priority-truck pings are getting starved." },
  dedup: { label: "2h dedup window", action: "Shorten the dedup window — same truck/user can't currently re-fire within 2h." },
  master_disabled: { label: "User master off", action: "Most users opted out of all notifications. Revisit your soft-prompt copy." },
  opt_out_daily_summary: { label: "Opted out: daily summary", action: "Daily summary opt-outs are climbing. Consider sending it less often." },
  opt_out_live_alerts: { label: "Opted out: live alerts", action: "Live alerts opt-outs are climbing. Consider geo-fencing the trigger." },
  opt_out_high_activity: { label: "Opted out: high-activity", action: "High-activity opt-outs are climbing. Consider raising the threshold." },
  inactive_user: { label: "Inactive user (>7d)", action: "Inactive users are throttled probabilistically. Consider a re-engagement email instead of push." },
  stale_event: { label: "Stale event", action: "Trucks ended before the push fired. Tighten the truck-live cron interval." },
  no_active_subscription: { label: "No active subscription", action: "Users have a notif_pref but no active sub. Re-prompt during their next visit." },
  anonymous_subscription: { label: "Anonymous subscription", action: "Anon subs can't get personalized pushes. Encourage signup at the click point." },
};

function pct(n) {
  return `${Math.round(n * 100)}%`;
}

function SuppressionInsight({ top, dominant, totalSuppressed }) {
  const headline = top[0];
  const meta = REASON_COPY[headline.reason] || { label: headline.reason, action: null };
  const isAlert = !!dominant;

  return (
    <div
      className={`rounded-xl border p-3 ${
        isAlert
          ? "border-amber-500/40 bg-amber-500/5"
          : "border-border bg-muted/10"
      }`}
      data-testid="admin-push-suppression-insight"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1.5">
            {isAlert ? "🚫 Top block reason · 24h" : "Top block reasons · 24h"}
          </div>
          <div className="mt-1 text-sm">
            <span className={`font-bold tabular-nums ${isAlert ? "text-amber-700 dark:text-amber-300" : ""}`}>
              {pct(headline.share)}
            </span>{" "}
            blocked by{" "}
            <span className="font-semibold">{meta.label}</span>{" "}
            <span className="text-muted-foreground tabular-nums">({headline.count} of {totalSuppressed})</span>
          </div>
          {isAlert && meta.action && (
            <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80" data-testid="admin-push-suppression-action">
              {meta.action}
            </p>
          )}
        </div>
        {top.length > 1 && (
          <div className="flex flex-col items-end gap-0.5 text-[11px] text-muted-foreground">
            {top.slice(1).map((r) => {
              const m = REASON_COPY[r.reason] || { label: r.reason };
              return (
                <span key={r.reason} className="tabular-nums">
                  {pct(r.share)} · {m.label}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
